import {
  Badge,
  Button,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { FiSearch, FiSliders } from "react-icons/fi";
import Sort from "design-system/icons/Sort";
import FilterTag from "components/Tag/FilterTag";
import { ActiveFilter, SortOption } from "./types";
import SortControls from "./SortControls";

interface FilterSortSearchBarProps {
  // Search
  searchValue?: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;

  // Sort
  currentSort: string;
  defaultSort: string;
  onSortChange: (value: string) => void;
  sortOptions: SortOption[];
  hideSort?: boolean;

  // Filters
  quickFilters?: React.ReactNode;
  allFilters?: React.ReactNode;
  activeFilters: ActiveFilter[];

  // Reset
  onReset: () => void;
  hasActiveFilters: boolean;
}

export default function FilterSortSearchBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = "Search...",
  currentSort,
  defaultSort,
  onSortChange,
  sortOptions,
  hideSort = false,
  quickFilters,
  allFilters,
  activeFilters,
  onReset,
  hasActiveFilters,
}: FilterSortSearchBarProps) {
  const currentSortLabel = sortOptions.find((opt) => opt.value === currentSort)?.label || "Sort";
  const moreFiltersCount = activeFilters.length;

  return (
    <VStack align="stretch" spacing={3} width="100%">
      {/* Row 1: Search + Sort */}
      <HStack justifyContent="space-between" spacing={3}>
        <InputGroup size="sm" maxW="420px">
          <InputLeftElement pointerEvents="none" color="fg.muted" h="28px">
            <FiSearch size={14} />
          </InputLeftElement>
          <Input
            placeholder={searchPlaceholder}
            value={searchValue || ""}
            onChange={(e) => onSearchChange(e.target.value)}
            bg="bg.surface"
            h="28px"
            fontSize="13px"
            sx={{ borderRadius: "md" }}
          />
        </InputGroup>

        {!hideSort && (
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <Button
                variant="ghost"
                size="sm"
                h="28px"
                minH="28px"
                px={2.5}
                fontSize="13px"
                fontWeight="normal"
                leftIcon={<Icon as={Sort} boxSize={3.5} />}
              >
                {currentSortLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent width="auto">
              <PopoverArrow />
              <PopoverBody p={2}>
                <SortControls
                  currentSort={currentSort}
                  defaultSort={defaultSort}
                  onSortChange={onSortChange}
                  sortOptions={sortOptions}
                />
              </PopoverBody>
            </PopoverContent>
          </Popover>
        )}
      </HStack>

      {/* Row 2: Quick filters + More filters */}
      {(quickFilters || allFilters) && (
        <HStack spacing={1.5} flexWrap="wrap">
          {quickFilters}
          {allFilters && (
            <Popover placement="bottom-start">
              <PopoverTrigger>
                <Button
                  size="sm"
                  variant="outline"
                  colorScheme="gray"
                  color="gray.700"
                  h="28px"
                  minH="28px"
                  px={2.5}
                  fontSize="13px"
                  fontWeight="medium"
                  leftIcon={<Icon as={FiSliders} boxSize={3.5} />}
                >
                  <HStack spacing={2}>
                    <span>More filters</span>
                    {moreFiltersCount > 0 && (
                      <Badge
                        borderRadius="full"
                        bg="cdsIndigo.500"
                        color="white"
                        px={2}
                        fontSize="11px"
                      >
                        {moreFiltersCount}
                      </Badge>
                    )}
                  </HStack>
                </Button>
              </PopoverTrigger>
              <PopoverContent width="auto" maxW="600px" overflow="visible">
                <PopoverArrow />
                <PopoverBody p={3} overflow="visible">
                  <Wrap spacing={2}>{allFilters}</Wrap>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          )}
        </HStack>
      )}

      {/* Row 3: Active filter pills + Clear all */}
      {hasActiveFilters && (
        <Wrap spacing={2} align="center">
          {activeFilters.map((filter) => (
            <WrapItem key={filter.key}>
              <FilterTag label={filter.label} onRemove={filter.onRemove} />
            </WrapItem>
          ))}
          <WrapItem>
            <Button variant="link" size="sm" onClick={onReset} colorScheme="gray">
              Clear all
            </Button>
          </WrapItem>
        </Wrap>
      )}
    </VStack>
  );
}
